import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, FlaskConical, Search, FileText, GitCompare, X } from "lucide-react";
import api, { formatMoney, formatNumber, formatApiErrorDetail } from "../lib/api";
import { AbcdChart, TipoStock, Mensual, pctES } from "../components/labresumen/Charts";
import { ProductosTabla, ResumenParado, FamiliasTabla } from "../components/labresumen/Tablas";
import { Comparacion } from "../components/labresumen/Comparacion";
import { EntradasStockChart } from "../components/EntradasStockChart";
import { SubirArriba } from "../components/SubirArriba";
import { NotasLaboratorio } from "../components/NotasLaboratorio";

export const SEGS = [["all", "Todo"], ["farmacia", "Farmacia"], ["parafarmacia", "Parafarmacia"]];
const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function LabPicker({ labs, value, onPick, testid = "lab-resumen-search" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const list = useMemo(() => labs.filter((l) => norm(l.laboratorio).includes(norm(q))).slice(0, 30), [labs, q]);
  return (
    <div className="relative flex-1 min-w-[260px]">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input className="input w-full" style={{ paddingLeft: 34 }} placeholder={value || "Buscar laboratorio… (ej. ISDIN, Cinfa)"}
             value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
             onBlur={() => setTimeout(() => setOpen(false), 150)} data-testid={testid} />
      {open && list.length > 0 && (
        <div className="absolute z-30 mt-1 w-full max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-options`}>
          {list.map((l) => (
            <button key={l.laboratorio} onMouseDown={() => { onPick(l.laboratorio); setQ(""); setOpen(false); }}
                    className="w-full flex justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`lab-option-${l.laboratorio}`}>
              <span className="font-semibold truncate">{l.laboratorio}</span>
              <span className="text-xs text-slate-500 whitespace-nowrap">{formatMoney(l.ventas)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Kpi({ label, value, sub, testid, tone }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={"font-display text-2xl font-extrabold mt-1 " + (tone || "")}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Kpis({ d }) {
  const k = d.kpis;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="lab-kpis">
      <Kpi label="Ventas" value={formatMoney(k.ventas)} sub={`${formatNumber(k.uds)} uds · ${pctES(k.cuota_ventas)} de tus ventas`} testid="lab-kpi-ventas" />
      <Kpi label="Stock (PVP)" value={formatMoney(k.stock_pvp)} sub={`${formatMoney(k.stock_pmc)} a coste · ${formatNumber(k.refs_stock)} refs`} testid="lab-kpi-stock" />
      <Kpi label="Rotación" value={k.rotacion == null ? "—" : `${String(k.rotacion).replace(".", ",")}×`} sub={`Margen potencial ${pctES(k.margen_pct)}`} testid="lab-kpi-rotacion" />
      <Kpi label="Stock parado" value={formatMoney(d.parado.eur)} tone="text-red-600"
           sub={`D real >1 año: ${formatMoney(d.d_real.eur)} · más de 1 año: ${formatMoney(d.mas_1y.eur)}`} testid="lab-kpi-parado" />
    </div>
  );
}

function Resumen({ d }) {
  const lab = d.laboratorio;
  return (
    <div className="space-y-6">
      <Kpis d={d} />
      <EntradasStockChart lab={lab} segment={d.segment} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <AbcdChart rows={d.abcd} />
        <TipoStock grupos={d.grupos} />
      </div>
      <Mensual rows={d.mensual} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ProductosTabla title="Top productos más vendidos" items={d.top} lab={lab} testid="lab-top" filename="laboratorio-top"
                        cols={["descripcion", "abcd", "uds_vendidas", "tot_venta"]} />
        <FamiliasTabla rows={d.familias} lab={lab} />
      </div>
      <ProductosTabla title="Stock parado (clase D)" sub="Pasa el ratón por un producto para ver su última entrada y el stock que queda." items={d.parado.items} lab={lab}
                      testid="lab-parado" filename="laboratorio-stock-parado" hover resumen={<ResumenParado d={d.parado} />}
                      cols={["cn", "descripcion", "familia", "stock", "valor_pvp", "u_entrada"]} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ProductosTabla title="Stock D real (más de 1 año)" items={d.d_real.items} lab={lab} testid="lab-dreal" filename="laboratorio-stock-d-real"
                        hover resumen={<ResumenParado d={d.d_real} />} cols={["descripcion", "stock", "valor_pvp", "u_entrada"]} />
        <ProductosTabla title="A punto de caducar (90 días)" items={d.caducar.items} lab={lab} testid="lab-caducar" filename="laboratorio-caducidades"
                        hover resumen={<ResumenParado d={d.caducar} />} cols={["descripcion", "stock", "valor_pvp", "caducidad"]} />
      </div>
    </div>
  );
}

function useLabData(lab, seg) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!lab) { setData(null); setErr(""); return; }
    setLoading(true); setErr("");
    api.get("/labs-resumen", { params: { laboratorio: lab, segment: seg } }).then((r) => setData(r.data))
      .catch((e) => { setData(null); setErr(`${lab}: ${formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo cargar el resumen."}`); })
      .finally(() => setLoading(false));
  }, [lab, seg]);
  return { data, loading, err };
}

export default function ResumenLaboratorio() {
  const [labs, setLabs] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [lab, setLab] = useState(params.get("lab") || "");
  const [lab2, setLab2] = useState("");
  const [comparar, setComparar] = useState(false);
  const [seg, setSeg] = useState(params.get("segment") || "all");
  const [listErr, setListErr] = useState("");
  const [pdf, setPdf] = useState(false);
  const A = useLabData(lab, seg);
  const B = useLabData(comparar ? lab2 : "", seg);
  const data = A.data, loading = A.loading || B.loading, err = listErr || A.err || B.err;

  useEffect(() => {
    api.get("/labs-resumen/lista", { params: { segment: seg } }).then((r) => setLabs(r.data.labs))
      .catch((e) => setListErr(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo cargar la lista de laboratorios."));
  }, [seg]);
  const cerrarComparar = () => { setComparar(false); setLab2(""); };

  const descargar = async () => {
    setPdf(true);
    try {
      const r = await api.get("/labs-resumen/pdf", { params: { laboratorio: lab, segment: seg }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      a.download = `resumen-${lab}.pdf`; a.click();
    } catch { toast.error("No se pudo generar el PDF"); } finally { setPdf(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl fade-in" data-testid="lab-resumen-page">
      <SubirArriba />
      <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold flex items-center gap-1.5"><FlaskConical size={12} /> Análisis de stock</div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Resumen por laboratorio</h1>
      <p className="text-slate-500 mt-2 text-sm mb-6">Elige un laboratorio y verás su ABCD, ventas, stock parado, caducidades y evolución mes a mes.</p>

      <div className="card p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="lab-resumen-controls">
        <button onClick={() => navigate(`/labs?segment=${seg === "farmacia" ? "farmacia" : "parafarmacia"}`)} className="btn btn-outline btn-sm" data-testid="lab-resumen-volver">
          <ArrowLeft size={14} /> Volver al análisis
        </button>
        <LabPicker labs={labs || []} value={lab} onPick={setLab} />
        <div className="inline-flex bg-slate-100 border border-slate-200 rounded-lg p-0.5" data-testid="lab-resumen-segment">
          {SEGS.map(([k, l]) => (
            <button key={k} onClick={() => setSeg(k)} data-testid={`lab-resumen-seg-${k}`}
                    className={"px-3 py-1.5 text-xs font-semibold rounded-md transition-colors " + (seg === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{l}</button>
          ))}
        </div>
        {lab && data && !comparar && (
          <button onClick={() => setComparar(true)} className="btn btn-outline btn-sm" data-testid="lab-resumen-comparar">
            <GitCompare size={14} /> Comparar
          </button>
        )}
        {lab && data && !comparar && (
          <button onClick={descargar} disabled={pdf} className="btn btn-outline btn-sm" data-testid="lab-resumen-pdf">
            {pdf ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Descargar PDF
          </button>
        )}
        {comparar && (
          <div className="w-full flex items-center gap-3 flex-wrap pt-3 border-t border-slate-100" data-testid="lab-resumen-comparar-box">
            <span className="text-xs font-bold uppercase tracking-widest text-sky-700">Comparar con</span>
            <LabPicker labs={(labs || []).filter((l) => l.laboratorio !== lab)} value={lab2} onPick={setLab2} testid="lab-resumen-search-2" />
            <button onClick={cerrarComparar} className="btn btn-outline btn-sm" data-testid="lab-resumen-comparar-cerrar"><X size={14} /> Quitar comparación</button>
          </div>
        )}
      </div>

      {lab && <h2 className="font-display text-2xl font-extrabold mb-4" data-testid="lab-resumen-title">{lab}{comparar && lab2 ? <span className="text-sky-600"> vs {lab2}</span> : null}</h2>}
      {err && <div className="card p-6 text-slate-600 mb-6" data-testid="lab-resumen-error">{err} {!labs && <Link className="text-sky-700 font-semibold" to="/subidas/abcd">Subir estudio</Link>}</div>}
      {!lab && labs && !err && <div className="card p-10 text-center text-slate-500" data-testid="lab-resumen-empty">Busca un laboratorio para ver su resumen completo ({labs.length} disponibles).</div>}
      {comparar && !lab2 && data && <div className="card p-6 mb-6 text-center text-slate-500" data-testid="lab-resumen-comparar-vacio">Elige el segundo laboratorio para compararlo con {lab}.</div>}
      {loading && <Loader2 className="animate-spin text-slate-400" />}
      {!loading && data && comparar && B.data && <Comparacion a={data} b={B.data} />}
      {!loading && data && !(comparar && B.data) && <Resumen d={data} />}
      {!loading && data && !comparar && <div className="mt-6"><NotasLaboratorio lab={lab} /></div>}
    </div>
  );
}
